import React from "react";
import { ArrowRight } from "lucide-react";
import { RevealOnScroll } from "../shared";
import {
  INTERCEPTOR_HEADLINE,
  INTERCEPTOR_NARRATIVE,
  PIPELINE_STAGES,
  INFRASTRUCTURE_FEATURES,
} from "./howItWorksContent";

const NARRATIVE_STYLES = {
  "The Problem": {
    shell: "border-partner bg-[#11151F]",
    label: "text-partner-muted",
  },
  "The AI Employee in Action": {
    shell: "border-[#00D2FF]/35 bg-[#00D2FF]/8",
    label: "text-partner-accent",
  },
  "The Result": {
    shell: "border-[#00D2FF]/25 bg-[#11151F]",
    label: "text-partner",
  },
};

const PipelineRail = () => (
  <div
    className="flex flex-wrap items-center justify-center gap-2"
    data-testid="interceptor-pipeline"
  >
    {PIPELINE_STAGES.map((stage, index) => (
      <React.Fragment key={stage.id}>
        {index > 0 && <span className="text-partner-muted/60 text-xs">→</span>}
        <span
          className={`text-xs font-semibold px-2.5 py-1 rounded-full border whitespace-nowrap ${
            stage.ping
              ? "border-[#00D2FF]/50 text-partner-accent bg-[#00D2FF]/15"
              : "border-partner text-partner-muted bg-[#080A0F]"
          }`}
        >
          {stage.label}
        </span>
      </React.Fragment>
    ))}
  </div>
);

const HeroAnchor = () => (
  <div
    className="rounded-xl border border-[#00D2FF]/20 bg-[#11151F] p-5 sm:p-6 max-w-3xl mx-auto"
    data-testid="interceptor-hero-anchor"
  >
    <div className="text-center mb-5 pb-5 border-b border-partner">
      <p className="font-mono text-4xl sm:text-5xl font-bold text-partner-accent leading-none tabular-nums">
        9 sec
      </p>
      <p className="text-xs font-semibold text-partner mt-1.5">Lead capture</p>
    </div>
    <div className="text-center">
      <p className="text-[10px] font-mono text-partner-muted uppercase tracking-wider mb-3">
        Pipeline
      </p>
      <PipelineRail />
    </div>
  </div>
);

const NarrativeCard = ({ item }) => {
  const styles = NARRATIVE_STYLES[item.label] ?? NARRATIVE_STYLES["The Problem"];
  const isResult = item.label === "The Result";

  return (
    <div className={`rounded-xl border p-4 sm:p-5 h-full flex-1 min-w-0 ${styles.shell}`}>
      <p className={`text-xs font-semibold uppercase tracking-wider mb-2 text-center ${styles.label}`}>
        {item.label}
      </p>
      <p
        className={`text-sm font-body leading-relaxed text-center ${
          isResult ? "text-partner font-medium" : "text-partner-muted"
        }`}
      >
        {item.text}
      </p>
    </div>
  );
};

const NarrativeFlow = () => (
  <div
    className="flex flex-col md:flex-row items-stretch md:items-center gap-3 md:gap-2 w-full mb-8"
    data-testid="interceptor-narrative-flow"
  >
    {INTERCEPTOR_NARRATIVE.map((item, index) => (
      <React.Fragment key={item.label}>
        <NarrativeCard item={item} />
        {index < INTERCEPTOR_NARRATIVE.length - 1 && (
          <div className="flex items-center justify-center flex-shrink-0 py-1 md:py-0 md:px-1">
            <ArrowRight className="w-5 h-5 text-partner-accent rotate-90 md:rotate-0" />
          </div>
        )}
      </React.Fragment>
    ))}
  </div>
);

const STAGE_LABELS = Object.fromEntries(
  PIPELINE_STAGES.map((stage) => [stage.id, stage.label])
);

const FeatureTimelineStep = ({ feature, index, isLast }) => (
  <div
    className="flex gap-4 sm:gap-6 py-6 sm:py-7 border-b border-[#2A3348] last:border-b-0"
    data-testid={`interceptor-mission-${index}`}
  >
    <div className="flex flex-col items-center flex-shrink-0 w-14 sm:w-16">
      <span className="font-mono text-sm font-semibold text-partner-accent/70 tabular-nums">
        {String(index + 1).padStart(2, "0")}
      </span>
      <span className="text-[10px] text-[#9AA8BE] text-center mt-1 leading-tight">
        {STAGE_LABELS[feature.stage] ?? feature.stage}
      </span>
      {!isLast && (
        <div className="w-px flex-1 min-h-[1.5rem] bg-[#2A3348] mt-3" />
      )}
    </div>
    <div className="flex-1 min-w-0 pt-0.5">
      <h4 className="text-base sm:text-lg font-semibold text-[#C5D3E3] leading-snug mb-1.5">
        {feature.title}
      </h4>
      <p className="text-sm text-[#9AA8BE] font-body leading-snug mb-3">{feature.tagline}</p>
      <p className="text-sm text-partner/75 font-body leading-[1.75]">{feature.description}</p>
    </div>
  </div>
);

const FeatureTimeline = () => (
  <div className="max-w-3xl mx-auto" data-testid="interceptor-feature-timeline">
    <h3 className="text-center text-lg sm:text-xl font-semibold text-partner/90 mb-2">
      How each pipeline stage works
    </h3>
    <p className="text-center text-sm text-[#9AA8BE] font-body mb-6 sm:mb-8">
      Intercept → Qualify → Book → CRM Lock
    </p>
    <div className="rounded-xl border border-partner bg-[#11151F] px-5 sm:px-6 py-2">
      {INFRASTRUCTURE_FEATURES.map((feature, index) => (
        <FeatureTimelineStep
          key={feature.title}
          feature={feature}
          index={index}
          isLast={index === INFRASTRUCTURE_FEATURES.length - 1}
        />
      ))}
    </div>
  </div>
);

const InterceptorNodePanel = () => (
  <div className="interceptor-command relative" data-testid="interceptor-node-panel">
    <div className="relative border border-partner rounded-2xl overflow-hidden bg-[#080A0F]">
      <div className="px-5 sm:px-8 py-8 sm:py-10">
        <RevealOnScroll>
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight mb-2">
              {INTERCEPTOR_HEADLINE.title.split("AI Employee")[0]}
              <span className="gradient-text">AI Employee</span>
            </h2>
            <p className="text-base sm:text-lg text-partner-accent font-semibold font-body leading-snug mb-3">
              {INTERCEPTOR_HEADLINE.subtitle}
            </p>
            <p className="text-sm text-partner-muted font-body leading-relaxed mb-6">
              {INTERCEPTOR_HEADLINE.lead}
            </p>
            <HeroAnchor />
          </div>

          <NarrativeFlow />

          <FeatureTimeline />
        </RevealOnScroll>
      </div>
    </div>
  </div>
);

export default InterceptorNodePanel;
